// VectorShape state defaults. A box only stores what differs from these, plus
// whatever the settings dialog wrote; `resolveState` fills in the rest so the
// renderer never deals with partial objects.
import { getShape } from "./catalog/shapes";

// Studio brand purple ($brand / $brand-solid in elements_library/styles/_tokens.scss).
export const DEFAULT_COLOR = "rgba(127,86,217,1)";
export const DEFAULT_COLOR_2 = "rgba(169,99,245,1)";

const BASE = {
  shapeId: "rect",
  fill: {
    type: "solid",
    color: DEFAULT_COLOR,
    color2: DEFAULT_COLOR_2,
    angle: 90,
    transparency: 0,
  },
  stroke: {
    color: DEFAULT_COLOR,
    width: 0,
    dash: "solid",
    transparency: 0,
    startCap: "none",
    endCap: "none",
    lineCap: "butt",
  },
  radius: null,
  shadow: { enabled: false, x: 0, y: 4, blur: 12, color: "rgba(0,0,0,0.25)" },
  text: {
    value: "",
    font: "",
    size: 18,
    color: "rgba(255,255,255,1)",
    bold: false,
    italic: false,
    align: "center",
    valign: "middle",
    padding: 12,
  },
};

const GROUPS = ["fill", "stroke", "shadow", "text"];

export function defaultStateFor(shapeId) {
  const shape = getShape(shapeId) || getShape("rect");
  const state = {
    shapeId: shape.id,
    fill: { ...BASE.fill },
    stroke: { ...BASE.stroke },
    radius: null,
    shadow: { ...BASE.shadow },
    text: { ...BASE.text },
  };
  if (shape.kind === "line") {
    state.fill.type = "none";
    state.stroke.width = shape.strokeWidth;
    state.stroke.dash = shape.dash;
    state.stroke.startCap = shape.start;
    state.stroke.endCap = shape.end;
    state.stroke.lineCap = shape.cap;
  }
  if (shape.defaults) {
    GROUPS.forEach((g) => {
      if (shape.defaults[g]) state[g] = { ...state[g], ...shape.defaults[g] };
    });
  }
  return state;
}

export function resolveState(state) {
  const s = state || {};
  const base = defaultStateFor(s.shapeId);
  const out = { ...base, shapeId: base.shapeId };
  GROUPS.forEach((g) => {
    out[g] = { ...base[g], ...(s[g] || {}) };
  });
  // null = the shape's own default; "pill" / "squircle" are kept as keywords.
  if (s.radius == null || s.radius === "") out.radius = null;
  else if (s.radius === "pill" || s.radius === "squircle") out.radius = s.radius;
  else out.radius = Number(s.radius) || 0;
  return out;
}

const RGBA_RE = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;

// Scales a colour's alpha by (1 - transparency%). Non-rgba colours pass through.
export function withAlpha(color, transparency) {
  const t = Math.max(0, Math.min(100, Number(transparency) || 0));
  const m = RGBA_RE.exec(String(color || "").trim());
  if (!m) return color;
  const a = m[4] === undefined ? 1 : Number(m[4]);
  const alpha = Math.round(a * (1 - t / 100) * 1000) / 1000;
  return `rgba(${m[1]},${m[2]},${m[3]},${alpha})`;
}

// Effective corner radius in px: the box override, else the shape's default.
export function radiusOf(shape, state, w, h) {
  const r = state.radius == null ? shape.radius : state.radius;
  if (r === "pill") return Math.min(w, h) / 2;
  if (r === "squircle") return Math.min(w, h) * 0.3;
  return Math.max(0, Number(r) || 0);
}
